import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { useState } from 'react'

type FeatureBoxProps = {
  number: string
  title: string
  description: string
}

export default function FeatureBox({ number, title, description }: FeatureBoxProps) {
  const [isHovered, setIsHovered] = useState(false)
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })

  return (
    <motion.div
      ref={ref}
      className="relative overflow-hidden rounded-lg p-6 backdrop-blur-sm transition-all duration-300 hover:shadow-xl"
      style={{
        backgroundColor: isHovered ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.05)',
        boxShadow: isHovered
          ? '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          : '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
      }}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
      transition={{ duration: 0.4 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      whileHover={{ scale: 1.02 }}
    >
      <div className="relative z-10 flex flex-row items-start gap-4 md:flex-col md:items-start">
        {/* Number */}
        <motion.div
          className="flex-shrink-0 text-start text-4xl font-bold text-[#ff4500] md:text-5xl"
          initial={{ opacity: 0, scale: 0.5, rotate: -10 }}
          animate={
            inView ? { opacity: 1, scale: 1, rotate: 0 } : { opacity: 0, scale: 0.5, rotate: -10 }
          }
          transition={{ duration: 0.5, type: 'spring', bounce: 0.4 }}
        >
          {number}
        </motion.div>

        {/* Content */}
        <div className="flex-1">
          <motion.h3
            className="text-md mb-2 font-bold md:text-xl"
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.4, delay: 0.2 }}
            style={{
              textShadow: isHovered ? '0 0 8px rgba(255, 69, 0, 0.7)' : 'none',
              color: isHovered ? '#ff4500' : '#ffffff',
            }}
          >
            {title}
          </motion.h3>

          <motion.p
            className="text-xs leading-relaxed text-white md:text-base"
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, scale: 0.5 }}
            transition={{ duration: 0.4, delay: 0.3 }}
          >
            {description}
          </motion.p>

          <motion.div
            className="mx-auto mt-2 h-0.5 rounded-full bg-[#ff4500] md:mx-0"
            initial={{ width: 0 }}
            animate={{ width: isHovered ? '100%' : '40%' }}
            transition={{ duration: 0.3 }}
          />
        </div>
      </div>

      {/* Hover glow effect */}
      <div
        className="absolute inset-0 rounded-lg"
        style={{
          opacity: isHovered ? 0.2 : 0,
          background:
            'radial-gradient(circle at center, rgba(255, 69, 0, 0.6) 0%, rgba(0, 0, 0, 0) 70%)',
          filter: 'blur(15px)',
          transition: 'opacity 0.3s ease',
        }}
      />
    </motion.div>
  )
}
